import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Fish, Loader2, RefreshCw, Waves, Wind, Gauge, Clock, Moon, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_LOCATION = { lat: 35.2098048, lon: -86.0794285 };

function scoreTone(score, isDarkMode) {
  if (score >= 8) return isDarkMode ? "text-emerald-300" : "text-emerald-700";
  if (score >= 6) return isDarkMode ? "text-lime-300" : "text-lime-700";
  if (score >= 4) return isDarkMode ? "text-amber-300" : "text-amber-700";
  return isDarkMode ? "text-orange-300" : "text-orange-700";
}

function DetailCard({ icon: Icon, title, value, sub, isDarkMode, testId }) {
  return (
    <div data-testid={testId} className={`p-4 rounded-xl border ${isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-emerald-400" />
        <span className={`text-xs font-semibold ${isDarkMode ? "text-white/55" : "text-gray-500"}`}>{title}</span>
      </div>
      <p className="text-xl font-black">{value}</p>
      {sub && <p className={`text-xs mt-1 ${isDarkMode ? "text-white/45" : "text-gray-500"}`}>{sub}</p>}
    </div>
  );
}

export default function FishingConditionsPage() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [searchParams] = useSearchParams();
  const defaultLocation = user?.preferences?.default_location || FALLBACK_LOCATION;
  const lat = parseFloat(searchParams.get("lat") || defaultLocation.lat || FALLBACK_LOCATION.lat);
  const lon = parseFloat(searchParams.get("lon") || defaultLocation.lon || FALLBACK_LOCATION.lon);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(null);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const res = await axios.get(`${API}/subscription/status`, { timeout: 10000 });
        setIsPremium(res.data?.is_premium || res.data?.is_trial || user?.role === "admin");
      } catch {
        setIsPremium(user?.role === "admin" || user?.subscription?.plan === "premium" || user?.subscription?.status === "active");
      }
    };
    if (user) checkSubscription();
  }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/weather/fishing`, { params: { lat, lon }, timeout: 16000 });
      setData(response.data);
    } catch (error) {
      console.error("Fishing page error:", error.message);
    } finally {
      setLoading(false);
    }
  }, [lat, lon]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (isPremium === null || loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? "bg-[#0a0e1a]" : "bg-gray-50"}`} data-testid="fishing-page-loading">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center gap-4 ${isDarkMode ? "bg-[#0a0e1a] text-white" : "bg-gray-50 text-gray-900"}`} data-testid="fishing-page-premium-lock">
        <Fish className="w-16 h-16 text-emerald-400 opacity-50" />
        <h2 className="text-xl font-bold">Premium Feature</h2>
        <p className={isDarkMode ? "text-white/50" : "text-gray-500"}>Fishing conditions are available for premium subscribers.</p>
        <Link to="/subscription" data-testid="fishing-page-upgrade-link" className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-500 transition-colors">Upgrade to Premium</Link>
      </div>
    );
  }

  if (!data?.success) return null;

  const bite = data.bite || {};
  const water = data.water || {};
  const wx = data.weather || {};

  return (
    <div data-testid="fishing-conditions-page" className={`min-h-screen pb-12 ${isDarkMode ? "bg-[#0a0e1a] text-white" : "bg-gray-50 text-gray-900"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" data-testid="fishing-back-button" className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-white/60 hover:text-white" : "text-gray-500 hover:text-gray-900"} transition-colors`}>
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <button onClick={fetchData} data-testid="fishing-refresh-button" className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${isDarkMode ? "bg-white/10 hover:bg-white/15 text-white/70" : "bg-gray-200 hover:bg-gray-300 text-gray-600"} transition-colors`}>
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        <header className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2.5 rounded-xl ${isDarkMode ? "bg-emerald-500/20" : "bg-emerald-100"}`}>
              <Fish className={`w-6 h-6 ${isDarkMode ? "text-emerald-300" : "text-emerald-700"}`} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Fishing Conditions</h1>
              <p className={`text-sm ${isDarkMode ? "text-white/50" : "text-gray-500"}`}>Live bite read, USGS water data, species patterns, and prime windows</p>
            </div>
          </div>
        </header>

        <section className={`mb-8 rounded-2xl border p-5 ${isDarkMode ? "bg-emerald-500/10 border-emerald-400/20" : "bg-emerald-50 border-emerald-200"}`} data-testid="fishing-hero-assessment">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? "text-emerald-200" : "text-emerald-800"}`}>Current bite assessment</p>
              <h2 className={`text-5xl font-black leading-none mt-2 ${scoreTone(bite.score, isDarkMode)}`}>{bite.score}/10</h2>
              <p className="text-xl font-bold mt-1">{bite.rating}</p>
            </div>
            <p className={`max-w-2xl text-sm leading-relaxed ${isDarkMode ? "text-emerald-50/80" : "text-emerald-900"}`}>{bite.best_approach}</p>
          </div>
        </section>

        <section className="mb-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <DetailCard icon={Waves} title="Water Temp" value={water.water_temp_f ? `${water.water_temp_f}°F` : "Unavailable"} sub={water.flow_status} isDarkMode={isDarkMode} testId="fishing-page-water-temp" />
          <DetailCard icon={Wind} title="Wind" value={`${wx.wind_speed_mph ?? "—"} mph`} sub={`${wx.wind_direction} · ${wx.description}`} isDarkMode={isDarkMode} testId="fishing-page-wind" />
          <DetailCard icon={Gauge} title="Pressure" value={wx.pressure_hpa ? `${wx.pressure_hpa} hPa` : "—"} sub={`${bite.pressure_trend} trend`} isDarkMode={isDarkMode} testId="fishing-page-pressure" />
          <DetailCard icon={Moon} title="Moon" value={data.moon?.phase || "—"} sub={`${data.moon?.illumination ?? "—"}% illuminated`} isDarkMode={isDarkMode} testId="fishing-page-moon" />
        </section>

        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? "text-white/40" : "text-gray-400"}`}>Prime Windows</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.prime_windows?.map((window) => (
              <div key={window.label} data-testid={`fishing-page-window-${window.label.toLowerCase().replaceAll(" ", "-")}`} className={`p-5 rounded-2xl border ${isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}>
                <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-emerald-400" /><h3 className="font-semibold">{window.label}</h3></div>
                <p className="font-bold">{window.start} - {window.end}</p>
                <p className={`text-xs mt-2 ${isDarkMode ? "text-white/50" : "text-gray-500"}`}>{window.reason}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? "text-white/40" : "text-gray-400"}`}>Species Patterns</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.species?.map((item) => (
              <div key={item.species} data-testid={`fishing-page-species-${item.species.toLowerCase()}`} className={`p-5 rounded-2xl border ${isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">{item.species}</h3>
                  <span className={`font-black ${scoreTone(item.score, isDarkMode)}`}>{item.score}/10</span>
                </div>
                <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-gray-500"}`}>{item.pattern}</p>
              </div>
            ))}
          </div>
        </section>

        {water.nearest_stations?.length > 0 && (
          <section className="mb-8">
            <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? "text-white/40" : "text-gray-400"}`}>USGS Water Stations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {water.nearest_stations.slice(0, 6).map((station) => (
                <div key={station.site_code} data-testid={`fishing-page-station-${station.site_code}`} className={`p-4 rounded-2xl border ${isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}>
                  <p className="font-semibold truncate">{station.name}</p>
                  <p className={`text-xs mb-3 ${isDarkMode ? "text-white/40" : "text-gray-400"}`}>{station.distance_mi} mi · {station.site_code}</p>
                  <div className={`grid grid-cols-2 gap-2 text-xs ${isDarkMode ? "text-white/55" : "text-gray-600"}`}>
                    <span>Temp: {station.water_temp_f ? `${station.water_temp_f}°F` : "—"}</span>
                    <span>Flow: {station.discharge_cfs ? `${station.discharge_cfs} cfs` : "—"}</span>
                    <span>Stage: {station.gage_height_ft ? `${station.gage_height_ft} ft` : "—"}</span>
                    <span>Wading: {station.wadeability}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {bite.cautions?.length > 0 && (
          <section className={`rounded-2xl border p-5 ${isDarkMode ? "bg-amber-500/10 border-amber-400/20" : "bg-amber-50 border-amber-200"}`} data-testid="fishing-page-cautions">
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-amber-400" /><h2 className="font-semibold">Cautions</h2></div>
            {bite.cautions.map((caution) => <p key={caution} className={`text-sm ${isDarkMode ? "text-amber-50/80" : "text-amber-800"}`}>{caution}</p>)}
          </section>
        )}

        <p className={`text-center text-xs mt-8 ${isDarkMode ? "text-white/30" : "text-gray-400"}`} data-testid="fishing-page-source-note">Data source: {data.source}. {water.provisional_note}</p>
      </div>
    </div>
  );
}